import { useCallback, useState } from 'react';
import cn from 'classnames';

import { evSearchCrossSiteRecoClick } from 'events/recommendations';
import { track } from 'apis/amethyst';
import useMartyContext from 'hooks/useMartyContext';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import MelodyModal from 'components/common/MelodyModal';
import MelodyCarousel from 'components/common/MelodyCarousel';
import { capitalize, constructMSAImageUrl } from 'helpers';

import css from 'styles/components/search/crossSiteProducts.scss';

const PRODUCT_IMAGE_SIZE = 450;
const MSA_IMAGE_DIMENSION = { width: PRODUCT_IMAGE_SIZE, height: PRODUCT_IMAGE_SIZE };

export const CrossSiteProducts = ({ trustedRetailers = [], ProductComponent, getProductInfo, additionalClasses, msaImageParams, hydraSearchColorSwatches }) => {
  const { testId, marketplace: { name } } = useMartyContext();
  const [modal, setModal] = useState(null);

  const handleClickCard = useCallback((productObj, index, e) => {
    e.preventDefault();
    const product = { ...productObj, index };
    track(() => ([
      evSearchCrossSiteRecoClick, { product }
    ]));
    getProductInfo(productObj, { includeOos: true, errorOnOos: false }).then(response => {
      const data = { ...product, ...response };
      setModal(data);
    });
  }, [getProductInfo]);

  const handleClickStay = useCallback(product => {
    track(() => ([
      evSearchCrossSiteRecoClick, { product, proceedToTrustedRetailer: false }
    ]));
    setModal(null);
  }, []);

  const handleClickContinue = useCallback(product => {
    track(() => ([
      evSearchCrossSiteRecoClick, { product, proceedToTrustedRetailer: true }
    ]));
    setModal(null);
  }, []);

  function makeProductImage(modal) {
    const classes = { itemsContainer: css.itemsContainer, fullHeight: css.fullHeight };
    const arrowStyleOverrides = { top: '50%' };
    const carouseImages = modal.styles.find(v => v.styleId === modal.styleId);
    const imageUrls = carouseImages?.images ? carouseImages.images : [{ imageId: modal.thumbnailImageUrl }];
    const images = imageUrls.map((i, k) => <div key={k} className={cn(css.imageContainer)}>
      <img src={constructMSAImageUrl(i.imageId, MSA_IMAGE_DIMENSION)} alt={i.imageId} />
    </div>);

    return <MelodyCarousel classes={classes} arrowStyleOverrides={arrowStyleOverrides}>
      {images}
    </MelodyCarousel>;
  }

  const makeModal = modal => {
    const { storeUrl, storeName, productSeoUrl } = modal;
    return <MelodyModal
      onRequestClose={() => handleClickStay(modal)}
      className={css.modal}
      contentLabel="Trusted Retailers"
      isOpen={!!modal}>
      <section className={css.modalCarousel}>{makeProductImage(modal)}</section>
      <section className={css.modalContent}>
        <h2>En Voyage to <span className={css.uppercase}>{storeName}</span>.com</h2>
        <p>
          <a className={css.uppercase} href={storeUrl}>{storeName}</a> offers modern luxury with a playful twist.
        </p>
        <p>Login with your Zappos username and password.</p>
        <div className={css.modalBtnContainer}>
          <button
            data-test-id={testId('searchResultsCrossSiteModalStay')}
            type="button"
            className={css.btnStay}
            onClick={() => handleClickStay(modal)}>
            Stay on {capitalize(name)}
          </button>
          <a
            onClick={() => handleClickContinue(modal)}
            data-test-id={testId('searchResultsCrossSiteModalContinue')}
            className={css.btnContinue}
            href={productSeoUrl}
            target="_blank"
            rel="noopener noreferrer">
            Continue to {storeName}
          </a>
        </div>
      </section>
    </MelodyModal>;
  };

  if (trustedRetailers.length) {
    return (
      <>
        {trustedRetailers.map((v, i) => (
          <ProductComponent
            key={`${v.styleId}-${v.storeName}`}
            componentStyle="searchProductCrossSite"
            crossSite={v.storeName}
            onComponentClick={e => handleClickCard(v, i, e)}
            additionalClasses={additionalClasses}
            hydraSearchColorSwatches={hydraSearchColorSwatches}
            cardData={v}
            index={i}
            msaImageParams={msaImageParams}
            melodyCardTestId={testId('searchResultsCrossSiteProduct')}
            showRatingStars={false}
            shouldLazyLoad={false}/>
        ))}
        {!!modal && makeModal(modal)}
      </>
    );
  }

  return null;
};

export default withErrorBoundary('CrossSiteProducts', CrossSiteProducts);
